import { useEffect, useState } from 'react';
import type { AttributeDef, TaskStateProfile } from '@flexiplanner/types';
import { deriveTaskStateMode } from '@flexiplanner/types';
import type { CalendarEvent } from '@/stores/scheduleStore';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { formatTime } from '@/lib/utils';
import { EnumAttributeInput } from '@/components/attributes/EnumAttributeInput';
import { Loader2 } from 'lucide-react';

const emptyProfile: TaskStateProfile = {
  mode: 'neutral',
  required_productivity_energy: null,
  required_rejuvenation_energy: null,
  productivity_return: null,
  rejuvenation_return: null,
  resistance: null,
};

interface CalendarCommitmentEditorProps {
  event: CalendarEvent | null;
  open: boolean;
  onClose: () => void;
  onSave: (event: CalendarEvent) => void;
}

export function CalendarCommitmentEditor({
  event,
  open,
  onClose,
  onSave,
}: CalendarCommitmentEditorProps) {
  const [profile, setProfile] = useState<TaskStateProfile>(emptyProfile);
  const [customAttributes, setCustomAttributes] = useState<Record<string, unknown>>({});
  const [availableAttributes, setAvailableAttributes] = useState<AttributeDef[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [protectedDurationMinutes, setProtectedDurationMinutes] = useState(0);

  useEffect(() => {
    if (!open) return;
    setProfile({ ...emptyProfile, ...(event?.stateProfile || {}) });
    setCustomAttributes(event?.customAttributes || {});
    if (event?.start && event?.end) {
      setProtectedDurationMinutes(
        Math.max(5, Math.round((new Date(event.end).getTime() - new Date(event.start).getTime()) / 60000))
      );
    }
    void loadAttributes();
  }, [event, open]);

  async function loadAttributes() {
    const response = await api.user.getAttributes();
    if (response.success && Array.isArray(response.data)) {
      setAvailableAttributes(response.data);
    }
  }

  function updateProfile(key: StateScoreKey, value: string) {
    setProfile(current => {
      const nextProfile: TaskStateProfile = {
        ...current,
        [key]: value === '' ? null : Math.max(0, Math.min(10, Number(value))),
      };
      return {
        ...nextProfile,
        mode: deriveTaskStateMode(nextProfile),
      };
    });
  }

  function updateAttribute(attrId: string, value: unknown) {
    setCustomAttributes(current => ({
      ...current,
      [attrId]: value,
    }));
  }

  function handleAttributeDefinitionUpdated(updatedAttr: AttributeDef) {
    setAvailableAttributes(current =>
      current.map(attr => (attr.attr_id === updatedAttr.attr_id ? updatedAttr : attr))
    );
  }

  async function handleSave() {
    if (!event) return;
    setIsSaving(true);
    try {
      const response = await api.calendar.updateEvent(event.id, {
        state_profile: profile,
        custom_attributes: customAttributes,
        ...(event.isFlexHold && start
          ? {
              start: start.toISOString(),
              end: new Date(start.getTime() + protectedDurationMinutes * 60000).toISOString(),
            }
          : {}),
      });
      if (response.success && response.data) {
        onSave(normalizeCalendarEvent(response.data));
        onClose();
      }
    } finally {
      setIsSaving(false);
    }
  }

  const start = event ? new Date(event.start) : null;
  const end = event ? new Date(event.end) : null;
  const maxProtectedDuration =
    start && end ? Math.max(5, Math.round((end.getTime() - start.getTime()) / 60000)) : 0;

  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle>Calendar Commitment</DialogTitle>
          <DialogDescription>
            {event?.title || 'Google Calendar event'}
            {start && end ? ` · ${formatTime(start)} - ${formatTime(end)}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 py-2">
          {event?.isFlexHold && (
            <div className="grid gap-3 border-b pb-4">
              <div>
                <h4 className="text-sm font-semibold">Protected Time</h4>
                <p className="text-xs text-muted-foreground">
                  Shorten this hold when you want to return part of the window to the scheduler.
                </p>
              </div>
              <div className="grid gap-2 sm:max-w-[220px]">
                <Label>Protected duration</Label>
                <Input
                  type="number"
                  min={5}
                  max={maxProtectedDuration || undefined}
                  step={5}
                  value={protectedDurationMinutes || ''}
                  onChange={inputEvent => {
                    const value = Number(inputEvent.target.value);
                    if (!Number.isFinite(value)) {
                      setProtectedDurationMinutes(5);
                      return;
                    }
                    setProtectedDurationMinutes(
                      Math.max(5, Math.min(maxProtectedDuration || value, Math.round(value)))
                    );
                  }}
                />
              </div>
            </div>
          )}

          <div className="grid gap-4 border-b pb-4">
            <div>
              <h4 className="text-sm font-semibold">Actionable Energy</h4>
              <p className="text-xs text-muted-foreground">
                These values stay in Fluxiplanner and guide scheduling around this immovable commitment.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Mode</Label>
                <div className="flex h-10 items-center rounded-md border bg-muted/40 px-3 text-sm font-medium">
                  {formatMode(deriveTaskStateMode(profile))}
                </div>
              </div>
              <ScoreField
                label="Resistance"
                value={profile.resistance}
                onChange={value => updateProfile('resistance', value)}
              />
              <ScoreField
                label="Required P"
                value={profile.required_productivity_energy}
                onChange={value => updateProfile('required_productivity_energy', value)}
              />
              <ScoreField
                label="Required RJ"
                value={profile.required_rejuvenation_energy}
                onChange={value => updateProfile('required_rejuvenation_energy', value)}
              />
              <ScoreField
                label="P Return"
                value={profile.productivity_return}
                onChange={value => updateProfile('productivity_return', value)}
              />
              <ScoreField
                label="RJ Return"
                value={profile.rejuvenation_return}
                onChange={value => updateProfile('rejuvenation_return', value)}
              />
            </div>
          </div>

          <div className="grid gap-4">
            <div>
              <h4 className="text-sm font-semibold">Custom Attributes</h4>
              <p className="text-xs text-muted-foreground">
                Apply your existing specificity and weight fields to this calendar commitment.
              </p>
            </div>
            {availableAttributes.length === 0 ? (
              <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
                Create custom fields from an Activity editor, then use them here.
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {availableAttributes.map(attr => (
                  <div key={attr.attr_id} className="grid gap-2">
                    <Label>{attr.name}</Label>
                    {attr.data_type === 'enum' ? (
                      <EnumAttributeInput
                        attr={attr}
                        value={customAttributes[attr.attr_id] || ''}
                        onChange={value => updateAttribute(attr.attr_id, value)}
                        onAttributeUpdated={handleAttributeDefinitionUpdated}
                      />
                    ) : ['Number', 'float', 'integer'].includes(attr.data_type) ? (
                      <Input
                        type="number"
                        value={String(customAttributes[attr.attr_id] ?? '')}
                        onChange={inputEvent =>
                          updateAttribute(
                            attr.attr_id,
                            inputEvent.target.value === '' ? '' : Number(inputEvent.target.value)
                          )
                        }
                        placeholder="Numeric value"
                      />
                    ) : (
                      <Input
                        value={String(customAttributes[attr.attr_id] ?? '')}
                        onChange={inputEvent => updateAttribute(attr.attr_id, inputEvent.target.value)}
                        placeholder="Value"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !event}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Commitment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScoreField({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: number | null;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <Input
        type="number"
        min={0}
        max={10}
        step={0.5}
        value={value ?? ''}
        onChange={event => onChange(event.target.value)}
        placeholder="0-10"
      />
    </div>
  );
}

type StateScoreKey = Exclude<keyof TaskStateProfile, 'mode'>;

function formatMode(mode: NonNullable<TaskStateProfile['mode']>): string {
  switch (mode) {
    case 'productive':
      return 'Productive';
    case 'rejuvenating':
      return 'Rejuvenating';
    case 'both':
      return 'Both';
    default:
      return 'Neutral';
  }
}

function normalizeCalendarEvent(event: any): CalendarEvent {
  return {
    id: event.id,
    title: event.title || event.summary || 'Calendar event',
    start: event.start || event.start_time,
    end: event.end || event.end_time,
    isImmovable: Boolean(event.is_immovable ?? true),
    isFlexHold: Boolean(event.is_flex_hold),
    stateProfile: event.state_profile || null,
    customAttributes: event.custom_attributes || {},
  };
}
